import { RankingView } from './RankingView'
import { type ReactElement, type ReactNode, useState, Fragment } from 'react'
import clsx from 'clsx'
import type { TraceStep, TraceStepFull } from '../types'

export interface StepInspectorProps {
  step: TraceStep
  /** Click a `kg_files_touched` / `kg_files_returned` path → show
   *  the source for that file in the SourceViewer. */
  onFilePathClick?: (path: string) => void
  selectedFilePath?: string | null
  /** Fetch the full, untruncated payload for this step (AGNT-1064). Injected by
   *  the page (which owns the correlationId + KgTraceApi), mirroring how
   *  `onFilePathClick` keeps this component decoupled from the api client. When
   *  provided, a truncated step shows a "Load full payload" button. */
  onLoadFullPayload?: (stepId: string) => Promise<TraceStepFull | null>
}

/**
 * Inspector panel for a single trace step. Renders inputs / outputs
 * as collapsible JSON, plus per-step metadata (model, usage, files
 * touched, retry count). The companion SourceViewer reads the file
 * the user clicks on here.
 */
export function StepInspector({
  step,
  onFilePathClick,
  selectedFilePath,
  onLoadFullPayload,
}: StepInspectorProps): ReactElement {
  // Full-payload fetch (AGNT-1064): inputs/outputs are truncated to ~10 KiB
  // server-side; on demand we load the whole body and render it in place.
  const [full, setFull] = useState<TraceStepFull | null>(null)
  const [loadingFull, setLoadingFull] = useState(false)
  const [loadFullError, setLoadFullError] = useState<string | null>(null)

  // Effective payloads + truncation flags: once the full body is loaded, use it
  // and clear the truncated flags so the markers (and load button) disappear.
  // `input`/`output` are optional on TraceStepFull — fall back to the truncated
  // value (and keep the truncated flag) for any field the full payload omits, so
  // a field never blanks out (graphite #18118).
  const effInputs = full?.input ?? step.inputs
  const effOutputs = full?.output ?? step.outputs
  const inputsTruncated = full?.input !== undefined ? false : step.inputs_truncated
  const outputsTruncated = full?.output !== undefined ? false : step.outputs_truncated

  const canLoadFull =
    onLoadFullPayload !== undefined && (step.inputs_truncated || step.outputs_truncated)

  async function handleLoadFull(): Promise<void> {
    if (!onLoadFullPayload) return
    setLoadingFull(true)
    setLoadFullError(null)
    try {
      const res = await onLoadFullPayload(step.step_id)
      if (res) setFull(res)
      else setLoadFullError('Full payload not found.')
    } catch {
      setLoadFullError('Failed to load full payload.')
    } finally {
      setLoadingFull(false)
    }
  }

  // One shared notice element rendered at each truncated section. All copies
  // trigger the same load; on success `full` clears every truncated flag so they
  // vanish together. Plain text (no button) when the page didn't wire a loader.
  const truncationSlot = (
    <TruncationNotice
      canLoad={canLoadFull}
      loading={loadingFull}
      error={loadFullError}
      onLoad={handleLoadFull}
    />
  )

  // A readable view of the prompt the model was given, when this step's
  // `inputs` carries one (native `{ system, prompt }` or a kg-agent
  // `messages` array). Null for tool-call args / non-prompt payloads, which
  // keep the existing `inputs` JSON view untouched.
  const prompt = extractPrompt(effInputs)
  return (
    <div className="flex flex-col gap-4" data-testid="step-inspector">
      <header className="flex items-baseline gap-3">
        <h2 className="font-mono text-sm font-semibold">{step.tool_name}</h2>
        <StatusPill status={step.status} />
        <span className="text-xs text-muted-foreground ml-auto font-mono">{step.step_id}</span>
      </header>

      <MetaGrid step={step} />
      {(step.tool_name.endsWith('.ranking') || step.tool_name.endsWith('.cdoc') || step.tool_name.endsWith('.docs')) && (
        <CollapsibleSection title="ranking" defaultOpen>
          <RankingView payload={effOutputs} truncated={outputsTruncated} />
          {outputsTruncated && truncationSlot}
        </CollapsibleSection>
      )}
      {step.stage_metadata && Object.keys(step.stage_metadata).length > 0 && (
        <CollapsibleSection title="stage metadata" defaultOpen>
          <dl
            className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-xs font-mono"
            data-testid="step-inspector-stage-metadata"
          >
            {Object.entries(step.stage_metadata)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([k, v]) => (
                <Fragment key={k}>
                  <dt className="text-muted-foreground">{k}</dt>
                  <dd className="break-words">
                    {Array.isArray(v) ? v.join(', ') : typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v)}
                  </dd>
                </Fragment>
              ))}
          </dl>
        </CollapsibleSection>
      )}

      <FilePathsList
        title="kg_files_touched"
        paths={step.kg_files_touched}
        onClick={onFilePathClick}
        selectedPath={selectedFilePath}
      />
      <FilePathsList
        title="kg_files_returned"
        paths={step.kg_files_returned}
        onClick={onFilePathClick}
        selectedPath={selectedFilePath}
      />

      {step.error && (
        <CollapsibleSection title="error" defaultOpen>
          <pre
            className="bg-red-50 text-red-900 dark:bg-red-950 dark:text-red-200 text-xs p-3 rounded font-mono whitespace-pre-wrap break-words"
            data-testid="step-inspector-error"
          >
            {step.error}
          </pre>
        </CollapsibleSection>
      )}

      {prompt && (
        <CollapsibleSection title="prompt" defaultOpen>
          <PromptBlocks prompt={prompt} truncated={inputsTruncated} truncationSlot={truncationSlot} />
        </CollapsibleSection>
      )}

      <CollapsibleSection title="inputs" defaultOpen>
        <PayloadView
          payload={effInputs}
          truncated={inputsTruncated}
          truncationSlot={truncationSlot}
          testId="step-inspector-inputs"
          onFilePathClick={onFilePathClick}
          pathClickDisabledReason={enoentReasonFromStep(step)}
        />
      </CollapsibleSection>

      {step.step_kind === 'reasoning' && typeof effOutputs === 'string' ? (
        // Thought-stream step (`model.reasoning` span): the output IS the
        // model's reasoning text — render it as prose, not escaped JSON.
        <CollapsibleSection title="thinking" defaultOpen>
          <pre
            className="bg-violet-50 text-violet-900 dark:bg-violet-950 dark:text-violet-200 text-xs p-3 rounded font-mono whitespace-pre-wrap break-words"
            data-testid="step-inspector-thinking"
          >
            {effOutputs}
          </pre>
        </CollapsibleSection>
      ) : (
        <CollapsibleSection title="outputs" defaultOpen>
          <PayloadView
            payload={effOutputs}
            truncated={outputsTruncated}
            truncationSlot={truncationSlot}
            testId="step-inspector-outputs"
          />
        </CollapsibleSection>
      )}
    </div>
  )
}

/** One labeled segment of a prompt (a role-tagged message, or the
 *  system / user halves of a native `{ system, prompt }` pair). */
export interface PromptBlock {
  label: string
  text: string
}

/** A normalized, human-readable view of the prompt sent to the model,
 *  derived from a step's `inputs`. Shape-agnostic so the renderer doesn't
 *  care whether the source was a native `{ system, prompt }` object or a
 *  kg-agent `messages` array. */
export interface PromptView {
  blocks: PromptBlock[]
}

/**
 * Coerce a single message's `content` to display text. The Vercel AI SDK
 * model-message `content` is either a plain string or an array of parts
 * (`{ type: 'text', text }`, tool-call/tool-result parts, …). Join the text
 * parts; fall back to compact JSON for non-text parts so nothing is silently
 * dropped from the forensics view.
 */
function coerceMessageContent(content: unknown): string {
  if (typeof content === 'string') return content
  if (Array.isArray(content)) {
    return content
      .map((part) => {
        if (typeof part === 'string') return part
        if (part && typeof part === 'object' && 'text' in part && typeof part.text === 'string') {
          return part.text
        }
        return JSON.stringify(part)
      })
      .join('\n')
  }
  return JSON.stringify(content)
}

/**
 * Extract a readable prompt view from a step's `inputs`, or `null` when the
 * payload isn't a prompt (tool-call args, the truncation marker string, or
 * anything else) — in which case the caller keeps the raw `inputs` JSON view.
 *
 * Two recognized shapes, both produced by the model-logging wrappers'
 * `extractPromptInput` (kg-agent) / the native loop's stage `input`:
 *   - native:   `{ system: string, prompt: string }`  → System + User blocks
 *   - kg-agent: `[{ role, content }, …]` (messages)    → one block per message
 *
 * Exported for unit testing.
 */
export function extractPrompt(inputs: unknown): PromptView | null {
  // The backend replaces over-cap payloads with a marker string before it
  // reaches us — a plain string is never a prompt shape, so fall through to the
  // raw inputs view (which renders the marker + truncation hint).
  if (typeof inputs === 'string') return null

  // Native shape: { system, prompt } (both strings). Render the non-empty
  // halves so a future system-only or prompt-only payload still shows.
  if (
    inputs !== null &&
    typeof inputs === 'object' &&
    !Array.isArray(inputs) &&
    ('system' in inputs || 'prompt' in inputs)
  ) {
    const obj = inputs as { system?: unknown; prompt?: unknown }
    const blocks: PromptBlock[] = []
    if (typeof obj.system === 'string' && obj.system.length > 0) {
      blocks.push({ label: 'system', text: obj.system })
    }
    if (typeof obj.prompt === 'string' && obj.prompt.length > 0) {
      blocks.push({ label: 'user', text: obj.prompt })
    }
    return blocks.length > 0 ? { blocks } : null
  }

  // kg-agent shape: a messages array of `{ role, content }`. Require at least
  // one element that looks like a model message so a non-message array (e.g. a
  // tool result list) falls through to the raw view.
  if (Array.isArray(inputs)) {
    const blocks: PromptBlock[] = inputs
      .filter(
        (m): m is { role?: unknown; content: unknown } =>
          m !== null && typeof m === 'object' && 'content' in m,
      )
      .map((m) => ({
        label: typeof m.role === 'string' ? m.role : 'message',
        text: coerceMessageContent(m.content),
      }))
    return blocks.length > 0 ? { blocks } : null
  }

  return null
}

/**
 * Renders the prompt as labeled, readable blocks (System / User, or one per
 * role-tagged message) instead of raw JSON — the highest-leverage way to make
 * "what was the model asked" legible on the forensics page. Same monospace,
 * scrollable styling as PayloadView's pre.
 */
function PromptBlocks({
  prompt,
  truncated,
  truncationSlot,
}: {
  prompt: PromptView
  truncated: boolean
  truncationSlot?: ReactNode
}): ReactElement {
  return (
    <div className="flex flex-col gap-3" data-testid="step-inspector-prompt">
      {prompt.blocks.map((block, idx) => (
        <div key={`${block.label}-${idx}`} className="flex flex-col gap-1">
          <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            {block.label}
          </span>
          <pre
            className="bg-muted text-xs p-3 rounded font-mono whitespace-pre-wrap break-words max-h-96 overflow-y-auto"
            data-testid={`step-inspector-prompt-${block.label}-${idx}`}
          >
            {block.text}
          </pre>
        </div>
      ))}
      {truncated && truncationSlot}
    </div>
  )
}

function StatusPill({ status }: { status: 'ok' | 'error' }): ReactElement {
  return (
    <span
      className={clsx(
        'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
        status === 'ok' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800',
      )}
    >
      {status}
    </span>
  )
}

/**
 * Two-column grid of scalars: model, role, duration, usage, retry,
 * sub-agent routing reason, git_sha. Suppresses null/undefined rows
 * so the panel scales with the data the trace actually carries.
 */
function MetaGrid({ step }: { step: TraceStep }): ReactElement {
  const rows: { label: string; value: string }[] = []
  if (step.agent_role) rows.push({ label: 'agent', value: step.agent_role })
  if (step.model) rows.push({ label: 'model', value: step.model })
  if (step.duration_ms !== null)
    rows.push({ label: 'duration_ms', value: String(step.duration_ms) })
  if (step.usage) {
    if (step.usage.input !== undefined)
      rows.push({ label: 'tokens.input', value: step.usage.input.toLocaleString() })
    if (step.usage.output !== undefined)
      rows.push({ label: 'tokens.output', value: step.usage.output.toLocaleString() })
    if (step.usage.total !== undefined)
      rows.push({ label: 'tokens.total', value: step.usage.total.toLocaleString() })
  }
  if (step.tool_result_size_bytes !== null)
    rows.push({ label: 'result_bytes', value: step.tool_result_size_bytes.toLocaleString() })
  if (step.tool_result_truncated) rows.push({ label: 'result_truncated', value: 'true' })
  if (step.retry_count !== null && step.retry_count > 0)
    rows.push({ label: 'retry_count', value: String(step.retry_count) })
  if (step.subagent_routing_reason)
    rows.push({ label: 'routing_reason', value: step.subagent_routing_reason })
  if (step.git_sha)
    rows.push({ label: 'git_sha', value: step.git_sha.substring(0, 12) })

  if (rows.length === 0) return <></>

  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs" data-testid="step-inspector-meta">
      {rows.map((r) => (
        <div key={r.label} className="contents">
          <dt className="text-muted-foreground uppercase tracking-wide">{r.label}</dt>
          <dd className="font-mono">{r.value}</dd>
        </div>
      ))}
    </dl>
  )
}

interface FilePathsListProps {
  title: string
  paths: string[]
  onClick?: (path: string) => void
  selectedPath?: string | null
}

function FilePathsList({
  title,
  paths,
  onClick,
  selectedPath,
}: FilePathsListProps): ReactElement {
  if (paths.length === 0) return <></>
  return (
    <CollapsibleSection title={`${title} (${paths.length})`} defaultOpen>
      <ul className="flex flex-col gap-px" data-testid={`step-inspector-${title}`}>
        {paths.map((path) => (
          <li key={path}>
            <button
              type="button"
              onClick={() => onClick?.(path)}
              data-selected={path === selectedPath ? 'true' : 'false'}
              className={clsx(
                'w-full text-left font-mono text-xs px-2 py-1 rounded hover:bg-muted',
                path === selectedPath &&
                  'bg-blue-100 hover:bg-blue-100 dark:bg-blue-900 dark:hover:bg-blue-900',
              )}
            >
              {path}
            </button>
          </li>
        ))}
      </ul>
    </CollapsibleSection>
  )
}

interface PayloadViewProps {
  payload: unknown
  truncated: boolean
  /** Rendered below the payload when `truncated` — the "Load full payload"
   *  affordance (AGNT-1064). Supplied by StepInspector so this stays dumb. */
  truncationSlot?: ReactNode
  testId: string
  /** When provided, top-level string values whose KEY matches a known
   *  path field render as clickable buttons that fire this callback —
   *  same behavior as `kg_files_touched`, just sourced from the
   *  inputs payload. Lets users click `packages/equipment/tag-index.ts`
   *  in a grep `scope` to verify what is (or isn't) in that file. */
  onFilePathClick?: (path: string) => void
  /** When set, path-shaped values render as a non-clickable red badge
   *  with this short reason inline instead of a clickable button.
   *  Used to convey "the agent already tried this and got ENOENT" so
   *  the user doesn't fire a redundant fetch that's guaranteed to 404
   *  with a less informative error than the trace itself carries. */
  pathClickDisabledReason?: string
}

/**
 * Returns a short, user-facing reason when this step's failure means a
 * path-shaped input value can't be resolved by the SourceViewer either —
 * specifically: the agent's read got ENOENT. Returns `undefined` for
 * status=ok or non-ENOENT errors (timeouts, parse errors, etc.) where
 * the file might still exist and clicking is still useful.
 */
function enoentReasonFromStep(step: TraceStep): string | undefined {
  if (step.status !== 'error') return undefined
  if (!step.error) return undefined
  if (/ENOENT|no such file/i.test(step.error)) return 'file did not exist'
  return undefined
}

/**
 * Object keys we treat as file paths in tool inputs. Restricted set
 * (rather than a regex on values) so we don't accidentally render
 * tag identifiers or descriptions as clickable paths. Each tool's
 * input schema uses one of these for the path-shaped argument:
 *
 *   read_file:        `path`
 *   grep:             `scope` (or `path` alias)
 *   glob:             `pattern` is a glob, NOT a path → not in this set
 *   trace_connections: `tag` (subject of trace, not a path) → no
 *   summarize_doc:    `path`
 *   find_doc_sections: `path`
 */
const PATH_LIKE_INPUT_KEYS: ReadonlySet<string> = new Set([
  'path',
  'scope',
  'file_path',
  'node_path',
  'center_path',
  'centerPath',
])

/**
 * Glob meta-characters that disqualify a string from being a
 * concrete file path. Tools like `grep` accept `scope` as either a
 * literal file path (`packages/equipment/tag-index.ts`) OR a glob
 * (`packages/equipment/**`); the SourceViewer can only resolve a
 * literal path to one file, so render the value as plain text when
 * any glob meta is present. (Directory-only scopes like
 * `packages/equipment/` ALSO can't be resolved to a single file —
 * SourceViewer expects a file path — but rejecting bare directories
 * heuristically is brittle, so we leave them clickable today; the
 * worst case is a "File not found" toast in SourceViewer.) Greptile
 * #11554 follow-up; Copilot #11559 doc clarification.
 */
const GLOB_META_RE = /[*?[\]{}]/

function isConcreteFilePath(value: string): boolean {
  if (value.length === 0) return false
  if (GLOB_META_RE.test(value)) return false
  return true
}

/**
 * Renders the inputs/outputs blob. The backend already truncates
 * large payloads to 10 KiB and replaces them with a marker string;
 * we display it verbatim with a "load full payload" hint when the
 * truncated flag is set.
 *
 * For object payloads with a path-shaped key (see PATH_LIKE_INPUT_KEYS),
 * the value renders as a clickable button so users can navigate to
 * the searched scope without re-typing the path. Non-object payloads
 * render as JSON pre-text.
 */
function PayloadView({
  payload,
  truncated,
  truncationSlot,
  testId,
  onFilePathClick,
  pathClickDisabledReason,
}: PayloadViewProps): ReactElement {
  if (payload === undefined || payload === null) {
    return (
      <div className="text-xs text-muted-foreground italic" data-testid={testId}>
        (empty)
      </div>
    )
  }
  const isPlainObject =
    typeof payload === 'object' && !Array.isArray(payload) && payload !== null
  // Enter the structured view when there's anything path-meaningful to
  // do with the entries — either a click handler to fire OR a disable
  // reason that needs to render the ❌ badge. Without this disjunction
  // a caller embedding StepInspector without `onFilePathClick` would
  // silently lose the badge for ENOENT'd inputs (greptile P2 #11566).
  if (isPlainObject && (onFilePathClick || pathClickDisabledReason)) {
    const entries = Object.entries(payload as Record<string, unknown>)
    const hasClickablePath = entries.some(
      ([k, v]) =>
        PATH_LIKE_INPUT_KEYS.has(k) && typeof v === 'string' && isConcreteFilePath(v),
    )
    if (hasClickablePath) {
      return (
        <div className="flex flex-col gap-1">
          <div
            className="bg-muted text-xs p-3 rounded font-mono whitespace-pre-wrap break-words max-h-96 overflow-y-auto"
            data-testid={testId}
          >
            <span className="text-muted-foreground">{'{\n'}</span>
            {entries.map(([key, value], idx) => {
              const trailing = idx < entries.length - 1 ? ',' : ''
              const isPath =
                PATH_LIKE_INPUT_KEYS.has(key) &&
                typeof value === 'string' &&
                isConcreteFilePath(value)
              return (
                <div key={key} className="pl-4">
                  <span className="text-foreground">&quot;{key}&quot;</span>
                  <span className="text-muted-foreground">{': '}</span>
                  {isPath ? (
                    pathClickDisabledReason ? (
                      <span
                        className="text-red-700 dark:text-red-400"
                        data-testid={`${testId}-path-${key}-disabled`}
                        title={pathClickDisabledReason}
                      >
                        <span aria-hidden="true">❌ </span>
                        &quot;{value as string}&quot;
                        <span className="ml-2 text-xs italic text-red-600 dark:text-red-400">
                          — {pathClickDisabledReason}
                        </span>
                      </span>
                    ) : onFilePathClick ? (
                      <button
                        type="button"
                        onClick={() => onFilePathClick(value as string)}
                        className="text-blue-700 dark:text-blue-400 hover:underline"
                        data-testid={`${testId}-path-${key}`}
                      >
                        &quot;{value as string}&quot;
                      </button>
                    ) : (
                      // No click handler and no disable reason — render
                      // the value as plain text (the structured view
                      // entry was triggered by a sibling key's badge).
                      <span>{JSON.stringify(value)}</span>
                    )
                  ) : (
                    <span>{JSON.stringify(value)}</span>
                  )}
                  <span className="text-muted-foreground">{trailing}</span>
                </div>
              )
            })}
            <span className="text-muted-foreground">{'}'}</span>
          </div>
          {truncated && truncationSlot}
        </div>
      )
    }
  }
  const text = typeof payload === 'string' ? payload : JSON.stringify(payload, null, 2)
  return (
    <div className="flex flex-col gap-1">
      <pre
        className="bg-muted text-xs p-3 rounded font-mono whitespace-pre-wrap break-words max-h-96 overflow-y-auto"
        data-testid={testId}
      >
        {text}
      </pre>
      {truncated && truncationSlot}
    </div>
  )
}

/**
 * Truncation affordance (AGNT-1064): a "Load full payload" button when the page
 * wired a loader, with loading + error states; plain text otherwise. Rendered at
 * each truncated section but all copies share StepInspector's single fetch.
 */
function TruncationNotice({
  canLoad,
  loading,
  error,
  onLoad,
}: {
  canLoad: boolean
  loading: boolean
  error: string | null
  onLoad: () => void
}): ReactElement {
  return (
    <p
      className="text-xs text-amber-700 dark:text-amber-400 italic"
      data-testid="step-inspector-truncation"
    >
      Payload truncated (~10 KiB cap).{' '}
      {canLoad && (
        <button
          type="button"
          onClick={onLoad}
          disabled={loading}
          className="not-italic text-blue-700 dark:text-blue-400 hover:underline disabled:text-muted-foreground"
          data-testid="step-inspector-load-full"
        >
          {loading ? 'Loading…' : 'Load full payload'}
        </button>
      )}
      {error && <span className="not-italic text-red-600 dark:text-red-400"> — {error}</span>}
    </p>
  )
}

function CollapsibleSection({
  title,
  defaultOpen,
  children,
}: {
  title: string
  defaultOpen: boolean
  children: ReactNode
}): ReactElement {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <section className="border rounded">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium uppercase tracking-wide text-foreground hover:bg-muted"
        data-testid={`collapsible-${title}`}
      >
        <span>{title}</span>
        <span className="text-muted-foreground">{open ? '−' : '+'}</span>
      </button>
      {open && <div className="px-3 pb-3">{children}</div>}
    </section>
  )
}
